import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/site";

export const Navbar = ({ onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";
    const onAbout = location.pathname === "/about";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        if (id === "top") {
            if (onHome) onNavigate("top");
            else navigate("/");
            return;
        }
        if (onHome) onNavigate(id);
        else navigate(`/#${id}`);
    };

    const goAbout = () => {
        setOpen(false);
        navigate("/about");
    };

    const solid = scrolled || !onHome;

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                solid
                    ? "bg-[#14110F]/85 backdrop-blur-xl border-b border-[#2D2621]/70"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
                <button
                    data-testid="nav-logo-btn"
                    onClick={() => go("top")}
                    className="shrink-0"
                    aria-label="FnB International home"
                >
                    <Logo />
                </button>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="text-[13px] tracking-[0.14em] uppercase text-[#C2B7AD] hover:text-[#D4A373] transition-colors duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-link-about"
                        onClick={goAbout}
                        className={`text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${
                            onAbout ? "text-[#D4A373]" : "text-[#C2B7AD] hover:text-[#D4A373]"
                        }`}
                    >
                        About Us
                    </button>
                    <button
                        data-testid="nav-enquiry-btn"
                        onClick={() => go("enquiry")}
                        className="ml-1 px-5 py-2.5 rounded-sm border border-[#C58B49]/60 text-[#D4A373] text-[13px] tracking-[0.14em] uppercase hover:bg-[#C58B49] hover:text-[#0F0C08] transition-colors duration-300"
                    >
                        Wholesale Enquiry
                    </button>
                </nav>

                <button
                    data-testid="nav-menu-toggle"
                    className="md:hidden text-[#F4EFEA]"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25 }}
                        className="md:hidden bg-[#14110F]/95 backdrop-blur-xl border-b border-[#2D2621] px-6 py-6 flex flex-col gap-5"
                    >
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-mobile-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="text-left text-sm tracking-[0.14em] uppercase text-[#C2B7AD]"
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="nav-mobile-link-about"
                            onClick={goAbout}
                            className="text-left text-sm tracking-[0.14em] uppercase text-[#C2B7AD]"
                        >
                            About Us
                        </button>
                        <button
                            data-testid="nav-mobile-enquiry-btn"
                            onClick={() => go("enquiry")}
                            className="mt-2 px-5 py-3 rounded-sm border border-[#C58B49]/60 text-[#D4A373] text-[13px] tracking-[0.14em] uppercase"
                        >
                            Wholesale Enquiry
                        </button>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
